<template>
  <div class="col-md-6 offset-md-3">
    <div class="login-form mt-4 p-4">
      <div class="row g-3">
        <h4>Подтвердите, что вы владелец указанной почты</h4>
        <p>Вам на почту отправлено письмо с кодом, укажите пожалуйста тут этот код.</p>
        <p>Если что-то пошло не так, можно <a href="#" @click="handleClickActionRegisterResend" id="btn-resend">Отправить</a> повторно.</p>
        <div class="col-12 status-placeholder">
          <status-label :message="emailProgress" :style="emailProgressStyle"></status-label>
        </div>
        <div class="col-12">
          <label>Почта</label>
          <input type="email" name="email" class="form-control" placeholder="Email" v-model="email" readonly disabled>
        </div>
        <div class="col-12">
          <label>Код</label>
          <input type="text" name="code" class="form-control" placeholder="Code" v-model="code">
        </div>
        <div class="col-12 error-placeholder">
          <error-label/>
        </div>
        <div class="col-12">
          <button type="submit" class="btn btn-dark float-end ms-3" @click="handleClickActionRegisterConfirm">Подтвердить</button>
          <button type="submit" class="btn btn-dark float-end" @click="handleClickActionRegisterConfirmCancel">Отмена</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import ErrorLabel from "@/components/ui/ErrorLabel.vue";
import { latestMessage, type WebSocketMessageEmpty, type WebSocketMessageError, type WebSocketMessageWithFields } from "@/plugins/ws";
import StatusLabel from "@/components/ui/StatusLabel.vue";
import { useToast } from "@/composables/useToast";

defineOptions({
  name: 'ComponentsAuthRegisterCode',
});

interface EmailProgress {
  emailProgressMessage: string | null;
  emailProgressType: string | null;
}

interface User {
  email: string;
  register: EmailProgress;
}

interface RootState {
  auth: {
    user: User;
  };
  errorLabel: {
    errorReason: string | null;
  };
}

const emit = defineEmits<{
  switchMode: [mode: string];
}>();

const store = useStore<RootState>();

// Типы для WebSocket сообщений компонента RegisterCode
type RegisterConfirmMessage = WebSocketMessageEmpty<'auth-register-confirm'>;
type RegisterConfirmFailMessage = WebSocketMessageError<'auth-register-confirm-fail'>;
type RegisterEmailProgressMessage = WebSocketMessageWithFields<'auth-register-email-progress', {
  emailProgressMessage: string;
  emailProgressType: string;
}>;
type RegisterCancelMessage = WebSocketMessageEmpty<'auth-register-cancel'>;
type RegisterResendMessage = WebSocketMessageEmpty<'auth-register-resend'>;

// Объединенный тип для всех сообщений
type RegisterCodeWebSocketMessageType =
    | RegisterConfirmMessage
    | RegisterConfirmFailMessage
    | RegisterEmailProgressMessage
    | RegisterCancelMessage
    | RegisterResendMessage;

const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload: any) => void;

const email = ref('');
const code = ref('');
const emailProgress = ref('Prepare to sending ...');
const emailProgressStyle = ref('bg-info');
const latestMessageValue = ref(latestMessage);

const user = computed(() => store.state.auth.user);

const setErrorReason = (reason: string) => store.dispatch('errorLabel/setErrorReason', reason);
const unsetErrorReason = () => store.dispatch('errorLabel/unsetErrorReason');

const { showToast } = useToast();

watch(email, () => {
  unsetErrorReason();
});

watch(code, () => {
  unsetErrorReason();
});

// WebSocket message handler
const handleWebSocketMessage = (data: RegisterCodeWebSocketMessageType): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'auth-register-confirm':
      signalRegisterConfirm();
      break;
    case 'auth-register-confirm-fail':
      signalRegisterConfirmFail(data.reason);
      break;
    case 'auth-register-email-progress':
      signalEmailProgress(data.emailProgressMessage, data.emailProgressType);
      break;
    case 'auth-register-cancel':
      signalRegisterCancel();
      break;
    case 'auth-register-resend':
      signalRegisterResend();
      break;
  }
};

watch(latestMessageValue, handleWebSocketMessage);

const handleClickActionRegisterResend = () => {
  wsSend('authRegisterResend', {});
};

const handleClickActionRegisterConfirm = () => {
  wsSend('authRegisterCode', {
    code: code.value,
  });
};

const handleClickActionRegisterConfirmCancel = () => {
  wsSend('authRegisterCancel', {});
};

const signalRegisterConfirm = () => {
  unsetErrorReason();
  store.dispatch('auth/registerConfirm');
  store.dispatch('auth/setWelcomeScreen', true);
  showToast('Почта подтверждена', 'Регистрация завершена успешно', 5000);
  emit('switchMode', 'registerSuccess');
};

const signalRegisterConfirmFail = (payload: string) => {
  setErrorReason(payload);
  showToast('Ошибка подтверждения', payload, 15000);
};

const signalRegisterCancel = () => {
  unsetErrorReason();
  store.dispatch('auth/wsUnsetUser');
  emit('switchMode', 'register');
};

const signalEmailProgress = (message: string | null, type: string | null) => {
  unsetErrorReason();
  user.value.register.emailProgressMessage = message;
  user.value.register.emailProgressType = type;
  emailProgress.value = message === null ? 'Prepare to sending ...' : message;
  switch (type) {
    case 'error':
      emailProgressStyle.value = 'bg-danger';
      break;
    case null:
    case 'info':
    default:
      emailProgressStyle.value = 'bg-info';
      break;
  }
};

const signalRegisterResend = () => {
  emailProgress.value = 'Prepare to sending ...';
  emailProgressStyle.value = 'bg-info';
};

onMounted(() => {
  email.value = user.value.email;
  emailProgress.value = user.value.register.emailProgressMessage === null ? 'Prepare to sending ...' : user.value.register.emailProgressMessage;
  switch (user.value.register.emailProgressType) {
    case 'error':
      emailProgressStyle.value = 'bg-danger';
      break;
    case null:
    case 'info':
    default:
      emailProgressStyle.value = 'bg-info';
      break;
  }
});
</script>

<style scoped>

</style>